import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock3, ShieldAlert } from "lucide-react";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { track } from "../../lib/posthog";

function fmt(dateIso: string) {
  return new Date(dateIso).toLocaleString();
}

type BannerVariant = "danger" | "info" | "warning";

function bannerStyles(variant: BannerVariant) {
  const baseContainer =
    "fixed bottom-4 left-1/2 z-[70] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 rounded-lg bg-zinc-900/85 backdrop-blur-sm px-3 py-2.5 text-sm shadow-2xl";

  if (variant === "danger") {
    return {
      container: `${baseContainer} border border-rose-500/30`,
      iconWrap:
        "flex h-9 w-9 shrink-0 items-center justify-center rounded border border-rose-500/30 bg-rose-500/10",
      iconClass: "h-4 w-4 text-rose-300",
      titleClass: "text-sm font-medium text-rose-200",
      subtitleClass: "text-xs text-zinc-400",
    };
  }
  if (variant === "info") {
    return {
      container: `${baseContainer} border border-blue-500/30`,
      iconWrap:
        "flex h-9 w-9 shrink-0 items-center justify-center rounded border border-blue-500/30 bg-blue-500/10",
      iconClass: "h-4 w-4 text-blue-300",
      titleClass: "text-sm font-medium text-blue-200",
      subtitleClass: "text-xs text-zinc-400",
    };
  }
  return {
    container: `${baseContainer} border border-amber-500/30`,
    iconWrap:
      "flex h-9 w-9 shrink-0 items-center justify-center rounded border border-amber-500/30 bg-amber-500/10",
    iconClass: "h-4 w-4 text-amber-300",
    titleClass: "text-sm font-medium text-amber-200",
    subtitleClass: "text-xs text-zinc-400",
  };
}

export default function DemoAccessBanner() {
  const organizations = useOrganizationStore((s) => s.organizations);
  const activeOrgId = useOrganizationStore((s) => s.activeOrgId);
  const [now, setNow] = useState(Date.now());

  const activeOrg = useMemo(
    () => organizations.find((o) => o.orgId === activeOrgId),
    [organizations, activeOrgId],
  );
  const windows = activeOrg?.windows ?? [];

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (activeOrg?.isDemo) {
      track("demo_blocked_banner_shown", { org_id: activeOrg.orgId });
    }
  }, [activeOrg?.isDemo, activeOrg?.orgId]);

  if (!activeOrg?.isDemo) return null;

  const nowDate = new Date(now);
  const expires = activeOrg.demoExpiresAt ? new Date(activeOrg.demoExpiresAt) : null;
  if (expires && expires <= nowDate) {
    const styles = bannerStyles("danger");
    return (
      <div className={styles.container}>
        <div className="flex items-center gap-2.5">
          <div className={styles.iconWrap}>
            <ShieldAlert className={styles.iconClass} />
          </div>
          <div className="min-w-0">
            <p className={styles.titleClass}>Demo access expired</p>
            <p className={styles.subtitleClass}>
              Contact support to renew access for this organization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const activeWindow = windows.find((w) => {
    const start = new Date(w.startAt);
    const end = new Date(w.endAt);
    return start <= nowDate && end >= nowDate;
  });

  if (activeWindow) {
    const styles = bannerStyles("info");
    return (
      <div className={styles.container}>
        <div className="flex items-center gap-2.5">
          <div className={styles.iconWrap}>
            <Clock3 className={styles.iconClass} />
          </div>
          <div className="min-w-0">
            <p className={styles.titleClass}>Demo access is active</p>
            <p className={styles.subtitleClass}>Current window ends at {fmt(activeWindow.endAt)}.</p>
          </div>
        </div>
      </div>
    );
  }

  const nextWindow = windows
    .filter((w) => new Date(w.startAt) > nowDate)
    .sort((a, b) => +new Date(a.startAt) - +new Date(b.startAt))[0];
  const styles = bannerStyles("warning");

  return (
    <div className={styles.container}>
      <div className="flex items-center gap-2.5">
        <div className={styles.iconWrap}>
          <AlertTriangle className={styles.iconClass} />
        </div>
        <div className="min-w-0">
          <p className={styles.titleClass}>Demo access unavailable</p>
          <p className={styles.subtitleClass}>
            {nextWindow
              ? `Next access window opens at ${fmt(nextWindow.startAt)}.`
              : "Your demo access period has ended. Contact support to request a new access window."}
          </p>
        </div>
      </div>
    </div>
  );
}
